/**
 * Differences of this filter with HandledByFilter are:
 * - no owner filter also includes journeys with agents that are bots (ES lvl..)
 * - All and select options are only available for Manager agents
 * - Options names
 */
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { SegmentedButton } from '@ontruck/orc';
import AssignAgentSelector from 'modules/journey/AssignAgentSelector';

import { handledByFilter } from './constants';
import { StyledFilter, tasksHandledByFilterStyles } from './styled';
import { getSegmentedValue, values } from './HandledByFilter';

const t = {
  all: 'All',
  mine: 'Mine',
  no_owner: 'Unowned',
  select: 'Select',
};

function allowedToSeeRestrictions(me) {
  const groups = me?.groups;

  if (!me || !groups) return false;

  return ['Manager', 'ProdTech'].some((allowedGroup) => groups.includes(allowedGroup));
}

const TasksHandledByFilter = ({ value, filterUserGroups, onChange, counters, className }) => {
  const [selectAgentOpened, setSelectAgentOpened] = useState(false);
  const me = useSelector((state) => state.me);

  const segmentedValue = getSegmentedValue(value, me?.id);
  const showAllAndSelectAgent = allowedToSeeRestrictions(me);
  const isSelectOptionSelected =
    selectAgentOpened || (value !== null && typeof value === 'object' && value.id !== me?.id);

  const handleChange = useCallback(
    (_, { value }) => {
      if (!onChange) {
        return;
      }

      if (value === handledByFilter.select) {
        setSelectAgentOpened(true);
        return;
      }

      const realValue = values[value]({ me });

      onChange(realValue);
    },
    [onChange, me]
  );

  const handleSelectAgent = useCallback(
    ({ agent }) => {
      if (!onChange) {
        return;
      }

      if (!agent) {
        onChange(handledByFilter.no_owner);
        setSelectAgentOpened(false);
        return;
      }

      onChange({
        id: agent?.id,
        label: agent?.label,
        email: agent?.email,
        value: agent?.value,
      });
    },
    [onChange]
  );

  const handleBlur = useCallback(() => {
    if (!value || typeof value === 'string') {
      setSelectAgentOpened(false);
    }
  }, [value]);

  const renderLabel = useCallback(
    (field) => {
      const fields = {
        unowned: {
          text: t.no_owner,
          count: counters?.unowned,
        },
        all: {
          text: t.all,
          count: counters?.all,
        },
        me: {
          text: t.mine,
          count: counters?.me,
        },
        select: {
          text: t.select,
          counters: null,
        },
      };

      const currentField = fields[field];
      return (
        <>
          {currentField.text} {!!currentField.count && <>({currentField.count})</>}
        </>
      );
    },
    [counters]
  );

  return (
    <StyledFilter grow last>
      {!isSelectOptionSelected && (
        <div className={className}>
          <SegmentedButton.Parent size="M" value={segmentedValue} onChange={handleChange}>
            <SegmentedButton.Child
              value={handledByFilter.no_owner}
              label={renderLabel('unowned')}
            />
            <SegmentedButton.Child value={handledByFilter.mine} label={renderLabel('me')} />
            {showAllAndSelectAgent && (
              <SegmentedButton.Child value={handledByFilter.all} label={renderLabel('all')} />
            )}
            {showAllAndSelectAgent && (
              <SegmentedButton.Child value={handledByFilter.select} label={renderLabel('select')} />
            )}
          </SegmentedButton.Parent>
        </div>
      )}
      <div style={{ display: isSelectOptionSelected ? 'block' : 'none' }}>
        <AssignAgentSelector
          value={value}
          onChange={handleSelectAgent}
          minWidth={278}
          onBlur={handleBlur}
          autoFocus={false}
          selectRef={() => {
            /*noop*/
          }}
          filterUserGroups={filterUserGroups}
        />
      </div>
    </StyledFilter>
  );
};

TasksHandledByFilter.defaultProps = {
  value: handledByFilter.no_owner,
  fillContainer: false,
};

TasksHandledByFilter.propTypes = {
  value: PropTypes.any,
  fillContainer: PropTypes.bool,
};

TasksHandledByFilter.filterName = 'TasksHandledByFilter';

export default tasksHandledByFilterStyles(TasksHandledByFilter);
